import { useState, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Product } from '../../mocks/products';
import { CategoryItem } from '../../contexts/CategoryContext';

interface ProductArrangePanelProps {
  categoryList: CategoryItem[];
  dynamicProducts: Product[];
  onSave: (categoryOrder: CategoryItem[], productOrders: Record<string, Product[]>) => void;
  onCancel: () => void;
  saveTriggerId?: string;
}

// 흔들리는 애니메이션 스타일
const shakeStyle = `
  @keyframes wiggle {
    0%, 100% { transform: rotate(-1.5deg); }
    50% { transform: rotate(1.5deg); }
  }
  .wiggle {
    animation: wiggle 0.3s ease-in-out infinite;
  }
`;

function SortableProductCard({
  product,
  isArranging,
}: {
  product: Product;
  isArranging: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `product-${product.id}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 1,
    opacity: isDragging ? 0.3 : 1,
  };

  const volumeDisplay =
    product.volumeOptions && product.volumeOptions.length > 1
      ? product.volumeOptions.join(', ')
      : product.volume;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`group cursor-grab active:cursor-grabbing select-none ${
        isArranging ? 'wiggle' : ''
      }`}
    >
      <div className="w-full aspect-square rounded-lg overflow-hidden mb-3 md:mb-4 border-2 border-transparent group-hover:border-emerald-400 transition-colors">
        <img
          src={product.thumbnail}
          alt={product.name}
          className="w-full h-full object-cover object-top"
          loading="lazy"
          draggable={false}
        />
      </div>
      <p className="text-xs md:text-sm text-emerald-600 mb-1">{product.nameEn}</p>
      <h3 className="text-sm md:text-base font-medium text-gray-800 mb-1 md:mb-2">
        {product.name}
        <span className="text-xs md:text-sm font-normal text-gray-500 ml-1 md:ml-2">
          {volumeDisplay}
        </span>
      </h3>
      <p className="text-xs md:text-sm text-gray-500 leading-relaxed line-clamp-2">
        {product.recommendation}
      </p>
    </div>
  );
}

function DragOverlayCard({ product }: { product: Product }) {
  const volumeDisplay =
    product.volumeOptions && product.volumeOptions.length > 1
      ? product.volumeOptions.join(', ')
      : product.volume;
  return (
    <div className="cursor-grabbing select-none opacity-90 rotate-2">
      <div className="w-full aspect-square rounded-lg overflow-hidden mb-3 border-2 border-emerald-500">
        <img
          src={product.thumbnail}
          alt={product.name}
          className="w-full h-full object-cover object-top"
          draggable={false}
        />
      </div>
      <p className="text-xs text-emerald-600 mb-1">{product.nameEn}</p>
      <h3 className="text-sm font-medium text-gray-800">{product.name}
        <span className="text-xs font-normal text-gray-500 ml-1">{volumeDisplay}</span>
      </h3>
    </div>
  );
}

function SortableCategoryBlock({
  category,
  products,
  isArranging,
  onProductsChange,
}: {
  category: CategoryItem;
  products: Product[];
  isArranging: boolean;
  onProductsChange: (categoryId: string, newProducts: Product[]) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `category-${category.id}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 100 : 1,
    opacity: isDragging ? 0.5 : 1,
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const handleProductDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = products.findIndex((p) => `product-${p.id}` === active.id);
    const newIndex = products.findIndex((p) => `product-${p.id}` === over.id);
    if (oldIndex !== -1 && newIndex !== -1) {
      onProductsChange(category.id, arrayMove(products, oldIndex, newIndex));
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`mb-12 md:mb-20 ${isDragging ? 'ring-2 ring-emerald-400 rounded-xl p-4 bg-emerald-50' : ''}`}
    >
      {/* 카테고리 헤더 - 드래그 핸들 */}
      <div
        className={`mb-6 md:mb-10 text-center relative group cursor-grab active:cursor-grabbing select-none ${
          isArranging
            ? 'ring-2 ring-emerald-300 rounded-xl py-4 px-6 bg-white hover:ring-emerald-500 transition-all'
            : ''
        }`}
        {...(isArranging ? { ...attributes, ...listeners } : {})}
      >
        {isArranging && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 flex items-center gap-1 text-xs text-emerald-500 font-medium">
            <i className="ri-drag-move-2-fill"></i>
            카테고리 드래그
          </div>
        )}
        <h2 className={`text-xl md:text-3xl font-bold text-[#222222] mb-2 md:mb-3 ${isArranging ? 'mt-4' : ''}`}>
          {category.title}
        </h2>
        {category.description && (
          <p className="text-sm md:text-base text-gray-500 leading-relaxed">
            {category.description}
          </p>
        )}
      </div>

      {/* 제품 그리드 */}
      {products.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleProductDragEnd}
        >
          <SortableContext
            items={products.map((p) => `product-${p.id}`)}
            strategy={rectSortingStrategy}
          >
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 md:gap-x-8 gap-y-8 md:gap-y-12">
              {products.map((product) => (
                <SortableProductCard
                  key={product.id}
                  product={product}
                  isArranging={isArranging}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          <i className="ri-inbox-line text-4xl mb-3"></i>
          <p className="text-sm">아직 등록된 제품이 없습니다.</p>
        </div>
      )}
    </div>
  );
}

export default function ProductArrangePanel({
  categoryList,
  dynamicProducts,
  onSave,
  onCancel,
  saveTriggerId,
}: ProductArrangePanelProps) {
  const [localCategories, setLocalCategories] = useState<CategoryItem[]>(categoryList);
  const [localProductMap, setLocalProductMap] = useState<Record<string, Product[]>>(() => {
    const map: Record<string, Product[]> = {};
    categoryList.forEach((cat) => {
      map[cat.id] = dynamicProducts.filter((p) => p.category === cat.id);
    });
    return map;
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const handleProductsChange = useCallback(
    (categoryId: string, newProducts: Product[]) => {
      setLocalProductMap((prev) => ({ ...prev, [categoryId]: newProducts }));
    },
    []
  );

  const handleCategoryDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = localCategories.findIndex((c) => `category-${c.id}` === active.id);
    const newIndex = localCategories.findIndex((c) => `category-${c.id}` === over.id);
    if (oldIndex !== -1 && newIndex !== -1) {
      setLocalCategories((prev) => arrayMove(prev, oldIndex, newIndex));
    }
  };

  const handleSave = () => {
    onSave(localCategories, localProductMap);
  };

  return (
    <>
      <style>{shakeStyle}</style>
      {/* 숨겨진 저장 트리거 버튼 */}
      {saveTriggerId && (
        <button
          id={saveTriggerId}
          type="button"
          onClick={handleSave}
          className="hidden"
        />
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleCategoryDragEnd}
      >
        <SortableContext
          items={localCategories.map((c) => `category-${c.id}`)}
          strategy={verticalListSortingStrategy}
        >
          <div>
            {localCategories.map((category) => {
              const products = localProductMap[category.id] || [];
              if (products.length === 0 && !category.isCustom && !category.isDefault) return null;
              return (
                <SortableCategoryBlock
                  key={category.id}
                  category={category}
                  products={products}
                  isArranging={true}
                  onProductsChange={handleProductsChange}
                />
              );
            })}
          </div>
        </SortableContext>
      </DndContext>
    </>
  );
}